import React, { useEffect } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from '@mui/material/TextareaAutosize';

export type BreakdownDraft = { description: string; quantity: string; amount: string };

export const grossCents = (value: string): number | undefined => {
    const normalized = value.trim().replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return undefined;
    const amount = Math.round(Number(normalized) * 100);
    return Number.isSafeInteger(amount) ? amount : undefined;
};

export const InvoiceBreakdownEditor = ({ initial, total, onChange }: {
    initial: BreakdownDraft[];
    total: number;
    onChange: (lines: BreakdownDraft[]) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice.breakdown' });
    const { control, register, watch, getValues } = useForm<{ lines: BreakdownDraft[] }>({ defaultValues: { lines: initial } });
    const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
    const lines = watch('lines');
    useEffect(() => {
        const subscription = watch(() => onChange(getValues('lines')));
        return () => subscription.unsubscribe();
    }, [watch, getValues, onChange]);
    const assigned = lines.reduce((sum, line) => sum + (grossCents(line.amount) ?? 0), 0);
    return <div className='mt-2'>
        {fields.map((field, index) => <div key={field.id} className='invoice-breakdown-row mb-2'>
            <Form.Group controlId={`breakdown-${field.id}-text`}>
                <Form.Label>{t('description')}</Form.Label>
                <TextareaAutosize
                    id={`breakdown-${field.id}-text`}
                    className='form-control invoice-breakdown-description'
                    minRows={1}
                    maxLength={1000}
                    style={{ resize: 'none' }}
                    {...register(`lines.${index}.description` as const)}
                    value={lines[index]?.description || ''}
                />
            </Form.Group>
            <Form.Group controlId={`breakdown-${field.id}-quantity`}>
                <Form.Label>{t('quantity')}</Form.Label>
                <Form.Control type='number' min={1} max={1000000} step={1} {...register(`lines.${index}.quantity` as const)} />
            </Form.Group>
            <Form.Group controlId={`breakdown-${field.id}-amount`}>
                <Form.Label>{t('amount')}</Form.Label>
                <Form.Control inputMode='decimal' {...register(`lines.${index}.amount` as const)} />
            </Form.Group>
            <div>
                <div className='form-label d-none d-md-block' aria-hidden='true'>&nbsp;</div>
                <OverlayTrigger placement='top' overlay={<Tooltip id={`remove-breakdown-${field.id}`}>{t('remove')}</Tooltip>}>
                    <Button type='button' variant='outline-danger' aria-label={t('remove')} onClick={() => remove(index)}>
                        <FontAwesomeIcon icon={faTrashAlt} fixedWidth />
                    </Button>
                </OverlayTrigger>
            </div>
        </div>)}
        <Button variant='outline-primary' disabled={fields.length >= 100} onClick={() => append({ description: '', quantity: '1', amount: '' })}>{t('add')}</Button>
        <div className='mt-2'>{t('target')}: {(total / 100).toFixed(2)} EUR · {t('assigned')}: {(assigned / 100).toFixed(2)} EUR · <strong>{t('remaining')}: {((total - assigned) / 100).toFixed(2)} EUR</strong></div>
    </div>;
};
